import NextLink from "next/link";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import { componentName } from "@shared/utils/bem";
import CDNImage from "@shared/components/ui-kit/media/CDNImage/CDNImage.web";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import "./RefundProtectionClaim.css";

export type BaseRefundProtectionClaimProps = {
  hasError?: boolean;
  providerName?: string;
  refundTermsURL: string | null;
  onRefundRequest: (() => void) | null;
  onCancelProtection: (() => void) | null;
  withActionButtons?: boolean;
};

export function BaseRefundProtectionClaim({
  hasError,
  providerName,
  refundTermsURL,
  onRefundRequest,
  onCancelProtection,
  withActionButtons = true,
}: BaseRefundProtectionClaimProps) {
  const { t } = useTranslation("account");

  const bem = componentName("refund-protection-claim");

  return (
    <Box
      className={bem.className()}
      data-testid={`refund-protection-claim-container-${providerName}`}
    >
      <Box className={bem.element("info-container")}>
        <CDNImage
          src={formatRelativeStaticPath("/images/icons/shield_gold_3d.png")}
          alt="A gold shield to portray how your order is protected"
          width={40}
          height={40}
        />
        <Box className={bem.element("info-container__inner-container")}>
          {providerName ? (
            <Text className={bem.element("info-container__heading")}>
              {t("Your purchase is protected by")} {providerName}
            </Text>
          ) : (
            <Text className={bem.element("info-container__heading")}>
              {t("Your purchase is protected")}
            </Text>
          )}

          {refundTermsURL && (
            <NextLink
              href={refundTermsURL}
              className={bem.element(
                "info-container__inner-container__terms-url"
              )}
              target="_blank"
            >
              {t("Refund protection terms")}
            </NextLink>
          )}
        </Box>
      </Box>

      {withActionButtons && (onCancelProtection || onRefundRequest) && (
        <Box className={bem.element("buttons-container")}>
          {onCancelProtection && (
            <Box>
              <Button
                data-testid=""
                variant="outline"
                onClick={onCancelProtection}
              >
                {t("Cancel protection")}
              </Button>
            </Box>
          )}
          {onRefundRequest && (
            <Box>
              <Button
                data-testid=""
                variant="outline"
                onClick={onRefundRequest}
              >
                {t("Request refund")}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {hasError && (
        <Box>
          <Text className={bem.element("info-container__subheading")}>
            {t("There was an error getting your protection details.")}
          </Text>
        </Box>
      )}
    </Box>
  );
}
